import { useLayoutEffect, useRef, useState } from "react";
import { ArrowRight, Link2, Type } from "lucide-react";
import {
  SiYoutube,
  SiSubstack,
  SiSpotify,
  SiApplepodcasts,
  SiMedium,
  SiTed,
} from "react-icons/si";
import { cn } from "@/lib/utils";
import { HERO_CATEGORIES, THOUGHTS_CATEGORY, nw, type InputType } from "@/components/landing/landing-data";
import {
  TYPE_HOLD_TICKS,
  TYPE_TICK_MS,
  useLandingReducedMotion,
  useOnScreen,
  usePageVisible,
} from "@/components/landing/landing-motion";

const SOURCES = [
  { Icon: SiYoutube, label: "YouTube" },
  { Icon: SiSubstack, label: "Substack" },
  { Icon: SiSpotify, label: "Spotify" },
  { Icon: SiApplepodcasts, label: "Apple Podcasts" },
  { Icon: SiMedium, label: "Medium" },
  { Icon: SiTed, label: "TED" },
];

/** Decorative pastel washes behind the hero (lavender top, peach left, butter right, sky low). */
const WASHES = [
  { className: "left-1/2 top-[-160px] h-[520px] w-[1000px] -translate-x-1/2", color: "233 226 246", alpha: 0.75 },
  { className: "left-[-180px] top-[80px] h-[520px] w-[620px]", color: "251 231 220", alpha: 0.7 },
  { className: "right-[-180px] top-[40px] h-[520px] w-[620px]", color: "252 242 214", alpha: 0.75 },
  { className: "left-[22%] top-[680px] h-[440px] w-[720px]", color: "225 238 248", alpha: 0.6 },
];

export function HeroSection({ onUnfold }: { onUnfold: (value: string, type: InputType) => void }) {
  const [inputType, setInputType] = useState<InputType>("link");
  const [category, setCategory] = useState(0);
  const [value, setValue] = useState("");
  const [focused, setFocused] = useState(false);
  /** Auto-cycling stops for good the moment the visitor picks a category or mode. */
  const [autoCycle, setAutoCycle] = useState(true);
  const hasInput = Boolean(value.trim());

  const sectionRef = useRef<HTMLElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const reduce = useLandingReducedMotion();
  const onScreen = useOnScreen(sectionRef, 0.2);
  const pageVisible = usePageVisible();

  // V6 behaviour: the placeholder types itself out (one character per 60ms tick), holds
  // for 45 ticks, then moves to the next category. Written straight to the textarea so the
  // typing doesn't re-render the hero; only the category change is React state.
  const typing = autoCycle && !reduce && onScreen && pageVisible && !focused && !value;
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const full = HERO_CATEGORIES[category].placeholder;
    if (!typing) {
      el.placeholder = full;
      return;
    }
    let chars = 0;
    let hold = 0;
    el.placeholder = "";
    const id = window.setInterval(() => {
      if (chars < full.length) {
        chars += 1;
        el.placeholder = full.slice(0, chars);
      } else if (hold < TYPE_HOLD_TICKS) {
        hold += 1;
      } else {
        const next = (category + 1) % HERO_CATEGORIES.length;
        setCategory(next);
        setInputType(HERO_CATEGORIES[next].mode);
      }
    }, TYPE_TICK_MS);
    return () => window.clearInterval(id);
  }, [typing, category]);

  const chooseMode = (mode: InputType) => {
    setAutoCycle(false);
    setInputType(mode);
    if (mode === "text") setCategory(THOUGHTS_CATEGORY);
    else if (category === THOUGHTS_CATEGORY) setCategory(0);
  };

  const chooseCategory = (i: number) => {
    setAutoCycle(false);
    setCategory(i);
    setInputType(HERO_CATEGORIES[i].mode);
  };

  return (
    <section ref={sectionRef} aria-labelledby="lp-hero-title" className="relative overflow-hidden pb-16 pt-12 sm:pt-16 md:pb-20 md:pt-[clamp(20px,4svh,48px)]">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        {WASHES.map((w) => (
          <div
            key={w.className}
            className={cn("lp-wash", w.className)}
            style={{
              background: `radial-gradient(closest-side, rgb(${w.color} / ${w.alpha}), rgb(${w.color} / 0))`,
            }}
          />
        ))}
      </div>

      <div className="relative mx-auto flex max-w-[1200px] flex-col items-center px-4 text-center sm:px-8 lg:px-12">
        <h1 id="lp-hero-title" className="lp-h1">
          Where consumption <br className="hidden md:block" />
          becomes <span className="type-accent">momentum.</span>
        </h1>
        <p className="mt-[26px] max-w-[640px] text-lg leading-[1.45] text-(--lp-body) sm:mt-8 sm:text-[22px] md:mt-[22px]">
          {nw("Turn what you watch, read, and listen to into a deeper understanding of yourself.")}
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onUnfold(value, inputType);
          }}
          className="lp-composer mt-[38px] w-full max-w-[900px] text-left sm:mt-[46px] md:mt-[30px]"
        >
          <div role="group" aria-label="Input type" className="flex gap-2 border-b border-black/[0.07] p-3 sm:p-3.5 md:p-2.5">
            {(
              [
                { id: "link", Icon: Link2, label: "Link" },
                { id: "text", Icon: Type, label: "Text" },
              ] as const
            ).map(({ id, Icon, label }) => (
              <button
                key={id}
                type="button"
                className="lp-tab text-base sm:text-[17px]"
                aria-pressed={inputType === id}
                onClick={() => chooseMode(id)}
              >
                <Icon className="h-[18px] w-[18px]" aria-hidden /> {label}
              </button>
            ))}
          </div>

          <div className="px-5 pb-2 pt-5 sm:px-8 sm:pt-6 md:pt-5">
            <label htmlFor="lp-hero-input" className="sr-only">
              Paste a link or share a thought
            </label>
            <textarea
              id="lp-hero-input"
              ref={inputRef}
              rows={2}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              placeholder={HERO_CATEGORIES[category].placeholder}
              className="min-h-[96px] w-full resize-none bg-transparent text-base leading-[1.45] text-(--lp-ink) outline-none sm:min-h-[110px] sm:text-[19px] md:min-h-[76px]"
            />
          </div>

          <div className="flex flex-col-reverse items-stretch gap-3 border-t border-black/[0.07] p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-5 sm:py-5 sm:pl-8 sm:pr-5 md:py-3.5">
            <p className="text-center text-sm text-(--lp-muted) sm:text-left sm:text-base">
              {nw("Works with YouTube, Apple Podcasts, articles, and more.")}
            </p>
            <button type="submit" disabled={!hasInput} className="lp-go shrink-0 whitespace-nowrap px-4 text-[15px] min-[360px]:px-6 min-[360px]:text-base sm:pl-[30px] sm:pr-[26px] sm:text-[17px]">
              {nw("Begin reflection session")} <ArrowRight className="h-[18px] w-[18px]" aria-hidden />
            </button>
          </div>
        </form>

        <div
          role="group"
          aria-label="What are you unfolding?"
          className="mt-7 grid grid-cols-2 justify-items-center gap-x-6 sm:mt-10 md:mt-6 sm:flex sm:flex-wrap sm:items-center sm:justify-center sm:gap-x-4"
        >
          {HERO_CATEGORIES.map((c, i) => (
            <span key={c.label} className="flex items-center gap-2.5 sm:gap-4">
              {i > 0 && <span aria-hidden className="hidden h-[5px] w-[5px] bg-[#A89C92] sm:block" />}
              <button type="button" className="lp-cat" aria-pressed={category === i} onClick={() => chooseCategory(i)}>
                <span aria-hidden className="lp-bracket">[</span>
                {c.label}
                <span aria-hidden className="lp-bracket">]</span>
              </button>
            </span>
          ))}
        </div>
      </div>

      <div className="relative mx-auto mt-14 flex max-w-[1200px] flex-col items-center gap-6 px-4 sm:mt-[90px] sm:px-8 lg:px-12">
        <p className="lp-eyebrow text-center" style={{ letterSpacing: "0.28em" }}>
          {nw("Turn any of these into an Unfolding")}
        </p>
        <ul className="grid w-full grid-cols-3 gap-x-3 gap-y-5 text-[14px] sm:flex sm:justify-between sm:gap-[clamp(8px,2vw,24px)] sm:text-[clamp(14px,1.7vw,24px)]">
          {SOURCES.map(({ Icon, label }) => (
            <li key={label} className="lp-source flex flex-col items-center justify-center text-center sm:flex-row sm:whitespace-nowrap">
              <Icon className="h-[22px] w-[22px] shrink-0 sm:h-[1.1em] sm:w-[1.1em]" aria-hidden />
              {label}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
